import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Download, FileDoc, Share, X, Copy, CheckCircle } from "@phosphor-icons/react";
import ReportLoadingState from "@/components/ReportLoadingState";

const CATEGORY_LABELS = {
    license_visibility: "License Visibility",
    user_segmentation: "User Segmentation",
    governance: "Governance",
    cost_control: "Cost Control",
    future_readiness: "Future Readiness",
    procurement_readiness: "Procurement Readiness",
};
const CAT_ORDER = [
    "license_visibility",
    "user_segmentation",
    "governance",
    "cost_control",
    "future_readiness",
    "procurement_readiness",
];

function rag(score) {
    if (score == null) return "Amber";
    if (score < 40) return "Red";
    if (score < 70) return "Amber";
    return "Green";
}

function maturity(score) {
    if (score <= 25) return "Initial";
    if (score <= 50) return "Developing";
    if (score <= 75) return "Managed";
    return "Optimized";
}

// Render markdown with extra styling for FACT/ASSUMPTION/RISK/OPPORTUNITY lines.
// All inline formatting is rendered as React nodes (no dangerouslySetInnerHTML)
// so the AI-generated markdown can never inject arbitrary HTML.
function inlineNodes(text) {
    // Split on **bold**, *italic*, `code`, and [N] / [N, M] footnote citations.
    const out = [];
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[\d{1,3}(?:\s*,\s*\d{1,3})*\])/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > last) out.push(text.slice(last, m.index));
        const tok = m[0];
        if (tok.startsWith("**") && tok.endsWith("**")) {
            out.push(<strong key={`b-${key++}`}>{tok.slice(2, -2)}</strong>);
        } else if (tok.startsWith("`") && tok.endsWith("`")) {
            out.push(<code key={`c-${key++}`}>{tok.slice(1, -1)}</code>);
        } else if (tok.startsWith("[") && tok.endsWith("]")) {
            // Footnote citation → superscript, links to the References Used table anchor
            const inner = tok.slice(1, -1);
            const firstN = inner.split(",")[0].trim();
            out.push(
                <sup key={`fn-${key++}`} className="footnote-ref" data-testid={`footnote-ref-${firstN}`}>
                    <a href={`#ref-${firstN}`}>[{inner}]</a>
                </sup>
            );
        } else if (tok.startsWith("*") && tok.endsWith("*")) {
            out.push(<em key={`i-${key++}`}>{tok.slice(1, -1)}</em>);
        } else {
            out.push(tok);
        }
        last = m.index + tok.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

function renderMarkdown(md) {
    const lines = md.split("\n");
    const out = [];
    let listBuf = [];
    const flushList = () => {
        if (listBuf.length) {
            out.push(
                <ul key={`ul-${out.length}`}>
                    {listBuf.map((t, i) => (
                        <li key={`li-${out.length}-${i}`}>{inlineNodes(t)}</li>
                    ))}
                </ul>
            );
            listBuf = [];
        }
    };
    let tableBuf = null;
    const flushTable = () => {
        if (tableBuf) {
            const tIdx = out.length;
            out.push(
                <div key={`tw-${tIdx}`} className="my-5 overflow-x-auto">
                    <table className="w-full text-sm border border-[#2a2a2a]">
                        <thead className="bg-[#2ec5b6]">
                            <tr>
                                {tableBuf.header.map((h, i) => (
                                    <th key={i} className="text-left px-3 py-2.5 border-b border-[#2a2a2a] text-[#0a1f1d] font-bold uppercase text-[12px] tracking-[0.08em]">
                                        {inlineNodes(h)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableBuf.rows.map((r, ri) => (
                                <tr key={ri} className={ri % 2 ? "bg-[#0f0f0f]" : "bg-[#141414]"}>
                                    {r.map((c, ci) => (
                                        <td key={ci} className="px-3 py-2 border-b border-[#2a2a2a] text-[#d4d4d4] align-top">
                                            {inlineNodes(c)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
            tableBuf = null;
        }
    };
    const isTableRow = (l) => /^\|.*\|\s*$/.test(l.trim());
    const isTableSep = (l) => /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(l.trim());
    const splitRow = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

    for (let i = 0; i < lines.length; i++) {
        const raw = lines[i];
        const line = raw.replace(/\s+$/g, "");
        const k = `ln-${i}`;

        // Markdown table
        if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
            flushList();
            const header = splitRow(line);
            const rows = [];
            let j = i + 2;
            while (j < lines.length && isTableRow(lines[j])) {
                rows.push(splitRow(lines[j]));
                j++;
            }
            tableBuf = { header, rows };
            flushTable();
            i = j - 1;
            continue;
        }

        if (!line.trim()) {
            flushList();
            continue;
        }
        // Special FACT/ASSUMPTION/RISK/OPPORTUNITY blocks
        const m =
            /^\s*\*{0,2}\s*(FACT|ASSUMPTION|RISK|OPPORTUNITY)\s*\*{0,2}\s*:\s*(.*)$/i.exec(
                line
            );
        if (m) {
            flushList();
            const type = m[1].toUpperCase();
            // Strip any leaked leading/trailing markdown emphasis from the body
            const body = m[2]
                .replace(/^\*+\s*/, "")
                .replace(/\s*\*+$/, "")
                .trim();
            const kind = type.toLowerCase(); // fact | assumption | risk | opportunity
            out.push(
                <div key={k} className={`callout callout-${kind}`} data-testid={`report-callout-${kind}`}>
                    <span className={`callout-label callout-label-${kind}`}>{type}</span>
                    <span className="callout-body">{inlineNodes(body)}</span>
                </div>
            );
            continue;
        }
        if (line.startsWith("### ")) {
            flushList();
            out.push(<h3 key={k}>{line.slice(4)}</h3>);
            continue;
        }
        if (line.startsWith("## ")) {
            flushList();
            out.push(<h2 key={k}>{line.slice(3)}</h2>);
            continue;
        }
        if (line.startsWith("# ")) {
            flushList();
            out.push(<h1 key={k}>{line.slice(2)}</h1>);
            continue;
        }
        if (line.match(/^\s*[-*]\s+/)) {
            listBuf.push(line.replace(/^\s*[-*]\s+/, ""));
            continue;
        }
        flushList();
        out.push(<p key={k}>{inlineNodes(line)}</p>);
    }
    flushList();
    return out;
}

export default function Report() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [shareOpen, setShareOpen] = useState(false);
    const [shareInfo, setShareInfo] = useState(null); // {url, expires_at}
    const [shareLoading, setShareLoading] = useState(false);
    const [shareErr, setShareErr] = useState("");
    const [shareCopied, setShareCopied] = useState(false);
    const [shareDays, setShareDays] = useState(30);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get(`/assessments/${id}`);
                if (cancelled) return;
                setData(data);
                // If still generating, poll
                if (data.status === "generating") {
                    setTimeout(load, 3000);
                }
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/assessments/${id}/pdf`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `License_Risk_Scan_${data.organization_name}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const downloadDocx = async () => {
        try {
            const res = await api.get(`/assessments/${id}/docx`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(
                new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                })
            );
            const a = document.createElement("a");
            a.href = url;
            a.download = `License_Risk_Scan_${data.organization_name}.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const mintShareLink = async () => {
        setShareLoading(true);
        setShareErr("");
        try {
            const { data: resp } = await api.post(`/assessments/${id}/share`, {
                expires_in_days: Number(shareDays) || 30,
            });
            const url = `${window.location.origin}${resp.path}`;
            setShareInfo({ url, expires_at: resp.expires_at });
        } catch (e) {
            setShareErr(formatApiError(e));
        } finally {
            setShareLoading(false);
        }
    };

    const revokeShareLink = async () => {
        setShareLoading(true);
        setShareErr("");
        try {
            await api.delete(`/assessments/${id}/share`);
            setShareInfo(null);
            setShareCopied(false);
        } catch (e) {
            setShareErr(formatApiError(e));
        } finally {
            setShareLoading(false);
        }
    };

    const copyShareLink = async () => {
        if (!shareInfo?.url) return;
        try {
            await navigator.clipboard.writeText(shareInfo.url);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2500);
        } catch {
            // fall back — user can right-click / long-press
        }
    };

    if (err)
        return (
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16">
                <div className="block-risk" data-testid="report-error">{err}</div>
                <Link to="/dashboard" className="btn-secondary mt-6 inline-block">
                    Back to dashboard
                </Link>
            </div>
        );
    if (!data)
        return (
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 text-[#c2c2c2] uppercase tracking-widest text-sm">
                Loading report…
            </div>
        );

    const overall = data.scores?.overall ?? 0;
    const overallRag = rag(overall);
    const overallMaturity = maturity(overall);
    // A report is "displayable" once the backend has finished writing the
    // markdown. The backend writes `status: "ready"` after a successful
    // generation, but earlier code paths and seeded fixtures use `"complete"`.
    // Accept either so the action buttons (Share / DOCX / PDF) render
    // consistently regardless of which writer set the field.
    const isReportReady = ["ready", "complete"].includes(data?.status);

    return (
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-12" data-testid="report-page">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <Link
                    to="/dashboard"
                    className="btn-ghost inline-flex items-center gap-2"
                    data-testid="report-back-link"
                >
                    <ArrowLeft size={14} /> Dashboard
                </Link>
                <div className="flex flex-wrap items-center gap-3">
                    {isReportReady && (
                        <button
                            className="btn-secondary inline-flex items-center gap-2"
                            onClick={() => {
                                setShareOpen(true);
                                setShareErr("");
                            }}
                            data-testid="share-report-open"
                        >
                            <Share size={14} weight="bold" /> Share with client
                        </button>
                    )}
                    {isReportReady && (
                        <button
                            className="btn-secondary inline-flex items-center gap-2"
                            onClick={downloadDocx}
                            data-testid="download-docx-button-top"
                        >
                            <FileDoc size={14} weight="bold" /> Download .docx
                        </button>
                    )}
                    <button
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={downloadPdf}
                        data-testid="download-pdf-button"
                    >
                        <Download size={14} weight="bold" /> Download PDF
                    </button>
                </div>
            </div>

            {shareOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setShareOpen(false)}
                    data-testid="share-report-modal"
                >
                    <div
                        className="relative w-full max-w-lg border border-[#2ec5b6] bg-[#0a0a0a] p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute top-3 right-3 text-[#c2c2c2] hover:text-white"
                            onClick={() => setShareOpen(false)}
                            aria-label="Close"
                            data-testid="share-report-close"
                        >
                            <X size={18} />
                        </button>
                        <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                            Share this report with the client
                        </h2>
                        <p className="mt-3 text-sm text-[#c2c2c2] leading-relaxed">
                            Mint a tokenised, read-only URL that the client can open without an
                            account. The link expires automatically — you can revoke it anytime.
                        </p>
                        {!shareInfo && (
                            <div className="mt-6">
                                <label className="label" htmlFor="share-days">
                                    Link expires in (days)
                                </label>
                                <input
                                    id="share-days"
                                    type="number"
                                    min={1}
                                    max={365}
                                    className="input-base w-40"
                                    value={shareDays}
                                    onChange={(e) => setShareDays(e.target.value)}
                                    data-testid="share-report-days-input"
                                />
                                <button
                                    className="btn-primary mt-6 inline-flex items-center gap-2"
                                    onClick={mintShareLink}
                                    disabled={shareLoading}
                                    data-testid="share-report-generate"
                                >
                                    {shareLoading ? "Generating…" : "Generate share link"}
                                </button>
                            </div>
                        )}
                        {shareInfo && (
                            <div className="mt-6">
                                <label className="label">Read-only URL</label>
                                <div className="flex items-stretch gap-2">
                                    <input
                                        readOnly
                                        className="input-base flex-1 font-mono text-xs"
                                        value={shareInfo.url}
                                        data-testid="share-report-url"
                                        onFocus={(e) => e.target.select()}
                                    />
                                    <button
                                        className="btn-secondary inline-flex items-center gap-2"
                                        onClick={copyShareLink}
                                        data-testid="share-report-copy"
                                    >
                                        {shareCopied ? <CheckCircle size={14} /> : <Copy size={14} />}
                                        {shareCopied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                                <p className="mt-3 text-xs text-[#9a9a9a]">
                                    Expires{" "}
                                    <span data-testid="share-report-expires">
                                        {new Date(shareInfo.expires_at).toLocaleString(undefined, {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                </p>
                                <button
                                    className="btn-ghost text-xs mt-4"
                                    onClick={revokeShareLink}
                                    disabled={shareLoading}
                                    data-testid="share-report-revoke"
                                >
                                    Revoke this link
                                </button>
                            </div>
                        )}
                        {shareErr && (
                            <div className="block-risk text-sm mt-4" data-testid="share-report-error">
                                {shareErr}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cover panel */}
            <section className="border border-[#2a2a2a] bg-[#141414] p-8 md:p-10">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="overline">SmartNote2Self · License Risk Scan</div>
                    <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#ff4d6d] border border-[#ff4d6d]/40 bg-[#ff4d6d]/10 px-2 py-1" data-testid="report-confidentiality">
                        Confidential · Authorised Recipients Only
                    </div>
                </div>
                <h1 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mt-4">
                    {data.organization_name}
                </h1>
                <div className="text-[#c2c2c2] mt-3 text-sm">
                    {data.industry} · {data.user_count} users · {data.agreement_type}
                </div>
                {data.reference_used && (
                    <div className="mt-3 text-[12px] uppercase tracking-[0.14em] text-[#9a9a9a]" data-testid="report-reference-used">
                        Reference: Microsoft Modern Work Plan Comparison — {data.reference_used} (May 2026)
                    </div>
                )}

                <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    <div className="bg-[#0a0a0a] p-5">
                        <div className="overline">Overall</div>
                        <div className="font-mono text-4xl mt-1">{overall}</div>
                    </div>
                    <div className="bg-[#0a0a0a] p-5">
                        <div className="overline">Risk</div>
                        <div className="mt-3 flex items-center h-10" data-testid="report-overall-rag">
                            <span className={`rag-dot rag-dot-lg rag-dot-${overallRag}`} aria-label={`Overall RAG: ${overallRag}`} />
                            <span className="sr-only">{overallRag}</span>
                        </div>
                    </div>
                    <div className="bg-[#0a0a0a] p-5">
                        <div className="overline">Maturity</div>
                        <div className="font-display text-2xl mt-1">{overallMaturity}</div>
                    </div>
                    <div className="bg-[#0a0a0a] p-5" data-testid="report-confidence">
                        <div className="overline">Confidence</div>
                        <div className="font-display text-2xl mt-1">
                            {data.confidence || "Low"}
                        </div>
                    </div>
                    <div className="bg-[#0a0a0a] p-5">
                        <div className="overline">Generated</div>
                        <div className="text-sm mt-2 text-[#d4d4d4]">
                            {new Date(data.created_at).toLocaleString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-px grid grid-cols-2 md:grid-cols-6 gap-px bg-[#2a2a2a] border border-[#2a2a2a] border-t-0">
                    {CAT_ORDER.map((c) => {
                        const s = data.scores?.[c] ?? 0;
                        const r = rag(s);
                        return (
                            <div key={c} className="bg-[#0a0a0a] p-4 flex flex-col" data-testid={`score-${c}`}>
                                <div
                                    className="text-[12px] uppercase tracking-widest text-[#9a9a9a] leading-tight min-h-[2.4em]"
                                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                                >
                                    {CATEGORY_LABELS[c]}
                                </div>
                                <div className="font-mono text-2xl mt-3 flex items-center gap-3 leading-none">
                                    <span>{s}</span>
                                    <span
                                        className={`rag-dot rag-dot-lg rag-dot-${r}`}
                                        aria-label={`${CATEGORY_LABELS[c]} RAG: ${r}`}
                                        data-testid={`score-${c}-rag`}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Report body */}
            {data.status === "generating" || !(data.report_md || "").trim() ? (
                <ReportLoadingState />
            ) : (
                <article className="report-prose mt-10" data-testid="report-body">
                    {renderMarkdown(data.report_md || "")}
                </article>
            )}

            {/* References used — surfaced in-app for transparency */}
            {Array.isArray(data.references_used) && data.references_used.length > 0 && (
                <section className="mt-12 border-t border-[#2a2a2a] pt-8" data-testid="report-references-section">
                    <div className="overline mb-2">References used</div>
                    <h2 className="font-display text-2xl mb-2">What the AI was constrained to</h2>
                    <p className="text-[#c2c2c2] text-sm leading-relaxed max-w-3xl mb-6">
                        Listed for transparency and audit. These are <strong>not</strong> the only
                        basis for any single finding — uploaded evidence and the respondent&apos;s
                        own answers also shaped the narrative.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {data.references_used.map((r, idx) => {
                            const num = r.ref_number || idx + 1;
                            return (
                            <div
                                key={`${r.title}-${idx}`}
                                id={`ref-${num}`}
                                className="border border-[#2a2a2a] bg-[#141414] p-4 relative"
                                data-testid={`report-reference-${idx}`}
                            >
                                <div className="absolute top-3 right-4 font-mono text-[13px] text-[#2ec5b6] tabular-nums" data-testid={`reference-num-${num}`}>
                                    [{num}]
                                </div>
                                <div className="overline mb-1 pr-8">
                                    {(r.ref_type || r.kind || "ref").toUpperCase()} ·{" "}
                                    {r.category || "—"}
                                </div>
                                <div className="font-display text-base text-white leading-snug pr-8">
                                    {r.title}
                                </div>
                                <div className="text-[12px] text-[#9a9a9a] mt-2 space-y-0.5">
                                    {r.source_url && (
                                        <div className="truncate">
                                            <span className="text-[#7a7a7a]">URL:</span>{" "}
                                            <a
                                                href={r.source_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[#2ec5b6] hover:underline"
                                            >
                                                {r.source_url}
                                            </a>
                                        </div>
                                    )}
                                    {r.source_filename && (
                                        <div className="truncate">
                                            <span className="text-[#7a7a7a]">File:</span>{" "}
                                            <span className="font-mono text-[#d4d4d4]">
                                                {r.source_filename}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-[#7a7a7a]">Extracted:</span>{" "}
                                        <span className="text-white">{r.extraction_date || "—"}</span>
                                    </div>
                                </div>
                            </div>
                        );})}
                    </div>
                </section>
            )}

            <div className="mt-12 border-t border-[#2a2a2a] pt-6 flex justify-between gap-3 flex-wrap">
                <Link to="/dashboard" className="btn-secondary">
                    Back to dashboard
                </Link>
                <div className="flex flex-wrap gap-2">
                    {isReportReady && (
                        <button
                            className="btn-secondary inline-flex items-center gap-2"
                            onClick={() => {
                                setShareOpen(true);
                                setShareErr("");
                            }}
                            data-testid="share-report-open-bottom"
                        >
                            <Share size={14} weight="bold" /> Share with client
                        </button>
                    )}
                    <button
                        className="btn-secondary inline-flex items-center gap-2"
                        onClick={downloadDocx}
                        data-testid="download-docx-button-bottom"
                    >
                        <FileDoc size={14} weight="bold" /> Download .docx
                    </button>
                    <button
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={downloadPdf}
                        data-testid="download-pdf-button-bottom"
                    >
                        <Download size={14} weight="bold" /> Download PDF
                    </button>
                </div>
            </div>
        </div>
    );
}
